<template>
  <!--
    App 级 chrome：由 appChromeHost 挂到 body，Tab 切换不随页面销毁重建。
  -->
  <view class="app-chrome-root">
    <page-header
      v-show="headerShown"
      class="app-chrome-header"
      :show-back-button="false"
      :show-top-bar="true"
      :fixed="true"
      bg-color="transparent"
    />

    <custom-tabbar
      v-show="tabbarShown"
      class="app-chrome-tabbar"
      :current="currentTab"
      :menu-open="showTradeMenu"
      @change="handleTabChange"
      @trade-menu-open="handleTradeMenuOpen"
      @trade-menu-close="showTradeMenu = false"
    />

    <view class="app-chrome-modals">
      <trade-menu-modal
        :visible="showTradeMenu"
        :current-type="tradeType"
        @close="showTradeMenu = false"
        @select="handleTradeMenuSelect"
      />
      <login-modal />
      <session-auth-prompt v-if="showNativeSessionAuth" />
      <bind-invite-modal />
      <announcement-modal v-if="showNativeAnnouncement" />
    </view>

    <view class="ai-pet-host">
      <ai-pet :visible="petVisible" @open="openAiAssistant" />
    </view>
  </view>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/page-header/index.vue';
import CustomTabbar from '@/components/custom-tabbar/index.vue';
import LoginModal from '@/components/login-modal/index.vue';
import SessionAuthPrompt from '@/components/session-auth-prompt/index.vue';
import BindInviteModal from '@/components/bind-invite-modal/index.vue';
import AnnouncementModal from '@/components/announcement-modal/index.vue';
import TradeMenuModal from '@/components/trade-menu-modal/index.vue';
import AiPet from '@/components/ai-pet/index.vue';
import { isTabRequiresAuth, isUserAuthenticated, triggerLogin } from '@/common/auth';
import { useAppChromeStore, type AppChromeTab } from '@/store/appChrome';
import { useWalletStore } from '@/store/wallet';

const { t } = useI18n();

/** H5 公告/会话已有独立 body host；此处仅作非 H5 兜底 */
const showNativeAnnouncement = typeof document === 'undefined';
const showNativeSessionAuth = typeof document === 'undefined';

const chrome = useAppChromeStore();
const {
  visible: chromeVisible,
  headerVisible,
  currentTab,
  tradeType,
  isTabRoute,
} = storeToRefs(chrome);

const showTradeMenu = ref(false);
const headerShown = computed(() => headerVisible.value);
const tabbarShown = computed(() => chromeVisible.value);
const petVisible = computed(() => isTabRoute.value);

const openAiAssistant = () => {
  uni.navigateTo({ url: '/pages/ai-assistant/index' });
};

const syncChrome = () => {
  // 显式传当前地址，避免栈顶与 hash 不一致；勿再用栈顶 show() 覆盖
  const hash =
    typeof window !== 'undefined' ? window.location.hash || '' : '';
  const fromHash = hash.startsWith('#') ? hash.slice(1) : '';
  if (fromHash) {
    chrome.syncFromRoute(fromHash);
    return;
  }
  chrome.syncFromRoute();
};

const goTabPage = (url: string) => {
  uni.redirectTo({
    url,
    fail: () => {
      uni.reLaunch({
        url,
        fail: (err) => console.error('[AppChrome] tab navigate failed:', err),
      });
    },
  });
};

const handleTabChange = (key: string) => {
  if (currentTab.value === key) return;

  if (isTabRequiresAuth(key) && !isUserAuthenticated()) {
    triggerLogin();
    return;
  }

  if (['home', 'trade', 'fund', 'assets'].includes(key)) {
    chrome.clearOverlay();
    chrome.setTab(key as AppChromeTab);
    showTradeMenu.value = false;
    goTabPage(`/pages/${key}/index`);
  }
};

const SHORTCUT_PAGES: Record<string, string> = {
  '/pages/aiprediction/index': 'prediction',
  '/pages/copy-trade/index': 'copyTrade',
  '/pages/create-market/index': 'createMarket',
};

const currentPagePath = () => {
  const pages = getCurrentPages();
  if (!pages.length) return '';
  return '/' + (pages[pages.length - 1].route || '');
};

const handleTradeMenuOpen = () => {
  const path = currentPagePath();
  const shortcutKey = SHORTCUT_PAGES[path];

  if (!shortcutKey) {
    chrome.clearOverlay();
    chrome.tradeType = 'prediction';
    uni.navigateTo({
      url: '/pages/aiprediction/index',
      fail: () => {
        uni.reLaunch({ url: '/pages/aiprediction/index' });
      },
    });
    return;
  }

  chrome.tradeType = shortcutKey;
  showTradeMenu.value = true;
};

const handleTradeMenuSelect = (item: { key: string; route: string }) => {
  showTradeMenu.value = false;

  if (item.key === 'createMarket') {
    uni.showToast({ title: t('common.developing'), icon: 'none' });
    return;
  }

  chrome.tradeType = item.key;

  const pages = getCurrentPages();
  const currentRoute = pages.length
    ? '/' + (pages[pages.length - 1].route || '')
    : '';
  const targetPath = item.route.split('?')[0];

  if (currentRoute === targetPath) return;

  chrome.clearOverlay();
  uni.navigateTo({
    url: item.route,
    fail: () => {
      uni.reLaunch({
        url: item.route,
        fail: (err) => console.error('[AppChrome] trade menu nav failed:', err),
      });
    },
  });
};

const refreshAnnouncementsIfMainTab = () => {
  const pages = getCurrentPages();
  const route = pages.length ? pages[pages.length - 1]?.route || '' : '';
  if (!chrome.isMainTabbarPath(route)) return;
  if (!useWalletStore().isAuthenticated) return;
  const now = Date.now();
  const last = Number((window as any).__pantaAnnFetchAt || 0);
  if (now - last <= 15_000) return;
  try {
    (window as any).__pantaAnnFetchAt = now;
  } catch {
    /* ignore */
  }
  void import('@/store/announcements').then(({ useAnnouncementsStore }) => {
    const ann = useAnnouncementsStore();
    void ann.fetchActive();
    void ann.fetchUnreadCount();
  });
};

/** App 子树拿不到页面 onShow，靠导航拦截在切页后同步（勿 removeInterceptor，以免清掉 auth） */
const afterNavigate = () => {
  void nextTick(() => {
    showTradeMenu.value = false;
    syncChrome();
    refreshAnnouncementsIfMainTab();
  });
};

const NAV_APIS = [
  'navigateTo',
  'redirectTo',
  'reLaunch',
  'switchTab',
  'navigateBack',
] as const;

let navHooksInstalled = false;

onMounted(() => {
  syncChrome();
  refreshAnnouncementsIfMainTab();
  if (navHooksInstalled) return;
  navHooksInstalled = true;
  for (const api of NAV_APIS) {
    uni.addInterceptor(api, { success: afterNavigate });
  }
  // H5 浏览器前进/后退不走 uni 拦截器
  if (typeof window !== 'undefined') {
    window.addEventListener('hashchange', afterNavigate);
    window.addEventListener('popstate', afterNavigate);
  }
});
</script>

<style scoped lang="scss">
.app-chrome-root {
  /* 不占文档流；交互由各 fixed 子项自行接管 */
  position: fixed;
  inset: 0;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
  z-index: 10000;
}

.app-chrome-header {
  pointer-events: none;
  background: transparent !important;
  background-image: none !important;
}

.app-chrome-header :deep(.page-header-root),
.app-chrome-header :deep(.page-header-wrapper),
.app-chrome-header :deep(.lanhu-header),
.app-chrome-header :deep(.nav-bar) {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
}

.app-chrome-header :deep(.lanhu-header),
.app-chrome-header :deep(.hdr-btn),
.app-chrome-header :deep(.nav-bar),
.app-chrome-header :deep(.left-button),
.app-chrome-header :deep(.right-slot),
.app-chrome-header :deep(.sidebar-overlay) {
  pointer-events: auto;
}

.app-chrome-tabbar {
  pointer-events: none;
}

.app-chrome-tabbar :deep(.lanhu-tabbar) {
  pointer-events: none;
}

.app-chrome-tabbar :deep(.tabbar-shell),
.app-chrome-tabbar :deep(.center-fab) {
  pointer-events: auto;
}

.app-chrome-modals {
  pointer-events: none;
}

.app-chrome-modals :deep(.trade-menu-overlay),
.app-chrome-modals :deep(.login-modal-overlay),
.app-chrome-modals :deep(.login-status-bar),
.app-chrome-modals :deep(.lanhu-sheet-overlay) {
  pointer-events: auto;
}

.ai-pet-host {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(100vw, var(--app-max-width, 750px));
  z-index: 9980;
  overflow: visible;
  pointer-events: none;
}

.ai-pet-host :deep(.ai-pet) {
  pointer-events: auto;
}
</style>
